<template>
  <!-- Поля обёрнуты в <label> — связь с контролом есть и без id -->
  <!-- eslint-disable vuejs-accessibility/label-has-for -->
  <section v-if="visible" class="w-selection" aria-label="Выбранный интервал">
    <div class="w-selection-head">
      <div>
        <strong>{{ title }}</strong>
        <span class="w-small">{{ store.staffDoctor?.name }}</span>
      </div>
      <button
        class="w-button"
        type="button"
        aria-label="Закрыть выделение"
        @click="store.clearSelection()"
      >
        <q-icon name="close" />
      </button>
    </div>

    <div v-if="slot" class="w-actions">
      <span class="w-small">
        Кабинет {{ store.roomNumber(slot.roomId) }} · {{ durationMinutes(slot) }} мин.
      </span>
      <template v-if="appointment">
        <span>{{ patientName }}</span>
        <button
          v-if="store.canEdit"
          class="w-button danger"
          type="button"
          @click="store.cancelAppointment(appointment.id)"
        >
          Отменить запись
        </button>
      </template>
      <template v-else>
        <span v-if="availability?.blocked" class="w-error">{{ blockedReason }}</span>
        <template v-else>
          <span class="w-small">
            {{ store.patientId
              ? `Пациент №${store.patientId}`
              : 'Выберите пациента на основной странице' }}
          </span>
          <button
            v-if="store.canEdit"
            class="w-button primary"
            type="button"
            :disabled="!store.patientId"
            @click="store.book(slot.id)"
          >
            Записать пациента
          </button>
        </template>
        <button
          v-if="store.canEdit"
          class="w-button"
          type="button"
          @click="store.removeSlot(slot.id)"
        >
          Убрать приём
        </button>
      </template>
    </div>

    <div v-else-if="rest" class="w-actions">
      <span>{{ rest.label }}</span>
      <button
        v-if="store.canEdit"
        class="w-button"
        type="button"
        @click="store.removeBreak(rest.id)"
      >
        Убрать перерыв
      </button>
    </div>

    <div v-else-if="range" class="w-selection-fields">
      <label class="w-field">
        Дата
        <input v-model.lazy="range.date" type="date" />
      </label>
      <label class="w-field">
        С
        <input v-model.lazy="range.start" type="time" />
      </label>
      <label class="w-field">
        До
        <input v-model.lazy="range.end" type="time" />
      </label>
      <label class="w-field">
        Длительность, мин
        <input v-model.lazy.number="range.duration" type="number" min="1" max="480" step="1" />
      </label>
      <label class="w-field">
        Кабинет
        <select v-model.number="range.roomId">
          <option v-for="room in store.allowedRooms" :key="room.id" :value="room.id">
            {{ room.number ?? room.id }} · {{ room.purpose }}
          </option>
        </select>
      </label>
      <button
        class="w-button primary"
        type="button"
        :disabled="!store.canEdit"
        @click="store.addRange()"
      >
        Добавить приём
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useScheduleStore } from '@/store/calendar/scheduleModule';
import { durationMinutes, formatDate } from '~utils/calendarModel';

const store = useScheduleStore();

const slot = computed(() => store.selectedSlot);
const rest = computed(() => store.selectedBreak);
const range = computed(() => (store.selection?.kind === 'range' ? store.selection : null));

// Выделенный приём/перерыв мог исчезнуть после обновления — тогда панель не нужна
const visible = computed(() => !!(slot.value || rest.value || range.value));

const title = computed(() => {
  const { selection } = store;
  if (!selection) return '';
  const day = formatDate(selection.date, { weekday: 'short', day: 'numeric', month: 'long' });
  return `${day} · ${selection.start}–${selection.end}`;
});

const appointment = computed(() => (
  slot.value && store.schedule.appointments.find((item) => item.slotId === slot.value?.id)
));

const patientName = computed(() => {
  const patient = appointment.value?.patient;
  if (patient && typeof patient === 'object') return patient.name || `Пациент №${patient.id}`;
  return patient || `Пациент №${appointment.value?.patientId}`;
});

const availability = computed(() => slot.value && store.slotAvailability(slot.value));

const blockedReason = computed(() => {
  if (availability.value?.branchBlocked) return availability.value.branchReason;
  if (availability.value?.color === 'gray') return 'В этом интервале кабинет закрыт на перерыв.';
  return 'В этом интервале есть пациент другого врача.';
});
</script>
